/**
 * Curated list of major US-listed bitcoin ETFs and bitcoin-related equities
 * for the AddHoldingRow autocomplete on the Purchases tab (Patch AZ).
 *
 * Used by the autocomplete suggestion dropdown only. Once a ticker is
 * added as a holding, price polling uses the symbol verbatim through the
 * existing /api/etf-price proxy (Finnhub > Twelve Data > Yahoo > Stooq
 * waterfall); being in this list has no effect on what gets fetched.
 * Adding tickers here is free (bundled static data, no extra API calls);
 * the per-holding polling cost is unchanged regardless of list size.
 *
 * Adding to this list: append a {symbol, name, category} entry. Order
 * within a category doesn't matter; the autocomplete sorts matches by
 * relevance (symbol prefix first, then name substring).
 *
 * NOT exhaustive. Users can still type any ticker they want into the
 * input and submit it; the list only powers the suggestion dropdown.
 * A custom symbol just won't auto-populate the display name field.
 *
 * Coverage as of Patch AZ: all 13 US spot bitcoin ETFs trading at time
 * of writing, the dominant futures ETF (BITO), the popular 2x leveraged
 * (BITX), and Strategy (MSTR) as a bitcoin treasury equity since Paul
 * uses it as one of the existing two seed holdings. Miners (MARA, RIOT,
 * CLSK, etc.) intentionally excluded: their price behavior diverges
 * from BTC in ways that make them a different kind of position than
 * the spot ETFs this app is designed around.
 */

export type BitcoinTickerCategory = 'spot-etf' | 'futures-etf' | 'leveraged-etf' | 'equity';

export interface BitcoinTicker {
  symbol: string;
  name: string;
  category: BitcoinTickerCategory;
}

export const BITCOIN_TICKERS: BitcoinTicker[] = [
  // US spot bitcoin ETFs. The Jan 2024 cohort plus MSBT (Morgan Stanley,
  // launched April 2026). Listed roughly in order of AUM at the time of
  // this patch; order only affects how ties are presented in the
  // suggestion dropdown.
  { symbol: 'IBIT', name: 'iShares Bitcoin Trust', category: 'spot-etf' },
  { symbol: 'FBTC', name: 'Fidelity Wise Origin Bitcoin Fund', category: 'spot-etf' },
  { symbol: 'GBTC', name: 'Grayscale Bitcoin Trust', category: 'spot-etf' },
  { symbol: 'ARKB', name: 'ARK 21Shares Bitcoin ETF', category: 'spot-etf' },
  { symbol: 'BITB', name: 'Bitwise Bitcoin ETF', category: 'spot-etf' },
  { symbol: 'BTC',  name: 'Grayscale Bitcoin Mini Trust', category: 'spot-etf' },
  { symbol: 'HODL', name: 'VanEck Bitcoin ETF', category: 'spot-etf' },
  { symbol: 'BTCW', name: 'WisdomTree Bitcoin Fund', category: 'spot-etf' },
  { symbol: 'BTCO', name: 'Invesco Galaxy Bitcoin ETF', category: 'spot-etf' },
  { symbol: 'BRRR', name: 'Valkyrie Bitcoin Fund', category: 'spot-etf' },
  { symbol: 'EZBC', name: 'Franklin Bitcoin ETF', category: 'spot-etf' },
  { symbol: 'DEFI', name: 'Hashdex Bitcoin ETF', category: 'spot-etf' },
  { symbol: 'MSBT', name: 'Morgan Stanley Bitcoin Trust', category: 'spot-etf' },

  // Bitcoin futures ETFs. BITO is the largest by far; the others are
  // small enough that including them just adds dropdown noise.
  { symbol: 'BITO', name: 'ProShares Bitcoin Strategy ETF', category: 'futures-etf' },

  // Leveraged bitcoin ETFs. BITX (2x) is the dominant one; 1x and 3x
  // products exist but with thin volume.
  { symbol: 'BITX', name: 'Volatility Shares 2x Bitcoin Strategy ETF', category: 'leveraged-etf' },

  // Bitcoin treasury equities. Only MSTR for now; it's effectively a
  // levered BTC bet that some users hold instead of spot ETFs. Other
  // treasury companies (e.g. Marathon, Riot) are bitcoin miners and
  // have operational risk that decouples them from spot price; not
  // included here to avoid implying "miner = BTC ETF."
  { symbol: 'MSTR', name: 'Strategy', category: 'equity' },
];

/**
 * Filter helper for the autocomplete. Two-tier match ranking:
 *   - Symbol prefix matches first (typing "BIT" lists BITB/BITO/BITX
 *     before substring matches deeper in some name).
 *   - Name substring matches second (typing "fidelity" surfaces FBTC).
 *
 * Both tiers preserve original list order, so the highest-AUM ETFs
 * appear first within each tier. Case-insensitive. Empty query returns
 * the start of the list up to `limit` so the dropdown is useful on
 * focus before the user has typed anything.
 */
export function searchBitcoinTickers(query: string, limit = 8): BitcoinTicker[] {
  const q = query.trim().toUpperCase();
  if (q.length === 0) return BITCOIN_TICKERS.slice(0, limit);

  const prefixMatches: BitcoinTicker[] = [];
  const nameMatches: BitcoinTicker[] = [];
  for (const t of BITCOIN_TICKERS) {
    if (t.symbol.startsWith(q)) {
      prefixMatches.push(t);
    } else if (t.name.toUpperCase().includes(q)) {
      nameMatches.push(t);
    }
  }
  return [...prefixMatches, ...nameMatches].slice(0, limit);
}
